// @ts-strict-ignore
import React, { memo, useCallback } from 'react'
import { CardActions } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import RecordVideoButton from 'components/RecordVideoButton'
import Button from 'components/Button'
import { browserHistory } from 'services/router'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { useIsClient, useIsGuest, useIsMember } from 'hooks/user'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { QuestionActions_Question$key } from '__generated__/QuestionActions_Question.graphql'
import { QuestionActions_User$key } from '__generated__/QuestionActions_User.graphql'
import { useMediaQuery } from 'hooks/useMediaQuery'

const questionForNewJob = question => browserHistory.push('/signup/client?mode=job', `/signup/client?mode=job&question_title=${question.title}`)

interface IQuestionActionsProps {
  questionFragmentRef: QuestionActions_Question$key
  userFragmentRef: QuestionActions_User$key
  handleUse: (id: string) => void
  onVideoChanged: () => void
}

function QuestionActions({ questionFragmentRef, userFragmentRef, handleUse, onVideoChanged }: IQuestionActionsProps) {
  const theme = useTheme()
  const question = useFragment(graphql`
    fragment QuestionActions_Question on Question {
      id
      rawId
      title
      
      ...RecordVideoButton_Question
    }
  `, questionFragmentRef)

  const user = useFragment(graphql`
    fragment QuestionActions_User on User {
      ...user_useIsGuest
      ...user_useIsClient
      ...user_useIsMember
    }
  `, userFragmentRef)

  const guest = useIsGuest(user)
  const isMember = useIsMember(user)
  const isClient = useIsClient(user)
  const canAnswer = guest || isMember
  const canUse = guest || isClient

  const onUseClick = useCallback(() => (
    guest ? questionForNewJob(question) : handleUse(question?.id)
  ), [question])

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))

  if (!question) return null

  return (
    <CardActions style={isLargeScreen ? { padding: theme.spacing(4, 8, 8, 8) } : undefined}>
      {canAnswer && (
        <RecordVideoButton
          questionFragmentRef={question}
          onVideoReady={onVideoChanged}
        />
      )}

      {canUse && (
        <Button
          color={canAnswer ? undefined : 'primary'}
          onClick={onUseClick}
          data-cy={`use-question-${question.rawId}`}
        >
          <AssignmentIcon style={{ marginRight: theme.spacing(1) }} />
          Use this Question for a job
        </Button>
      )}
    </CardActions>
  )
}

export default memo(QuestionActions)
